import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/api/client';
import type { MeDto, TipusAcces } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { meMock, perfilMockDesat } from '@/auth/mockMe';
import { canviaIdioma, esIdioma } from '@/i18n';
import { MeContext, type MeState } from './MeContext';
import { tePermis, type ContextAmbit } from './permisos';

const ME_QUERY_KEY = ['me'] as const;

async function carregaMe(mode: 'msal' | 'mock'): Promise<MeDto> {
  if (mode === 'mock') return meMock(perfilMockDesat());
  return apiRequest<MeDto>('/me');
}

export function MeProvider({ children }: { children: ReactNode }) {
  const { autenticat, mode } = useAuth();
  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => carregaMe(mode),
    enabled: autenticat,
    staleTime: 60_000,
  });
  const me = query.data ?? null;

  useEffect(() => {
    if (me?.idioma && esIdioma(me.idioma)) void canviaIdioma(me.idioma);
  }, [me?.idioma]);

  const pot = useCallback(
    (recurs: string, tipus: TipusAcces, ctx?: ContextAmbit) => tePermis(me, recurs, tipus, ctx),
    [me],
  );
  const { refetch } = query;
  const reintenta = useCallback(() => void refetch(), [refetch]);

  const valor = useMemo<MeState>(
    () => ({ me, carregant: query.isLoading, error: query.error, reintenta, pot }),
    [me, query.isLoading, query.error, reintenta, pot],
  );
  return <MeContext.Provider value={valor}>{children}</MeContext.Provider>;
}
